// src/pages/PDV/PDV.js
import React, { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebaseConfig';
import { useAuth } from '../../auth/AuthContext';

import CarrinhoItem from '../../components/CarrinhoItem/CarrinhoItem';
import CarrinhoResumo from '../../components/CarrinhoResumo/CarrinhoResumo';
import ProdutoModal from '../../components/ProdutoModal/ProdutoModal';
import './PDV.css';
import '../../components/ProdutoCard/ProdutoCard.css';

// Componente do Dashboard com título atualizado
const VendedorDashboard = ({ totalVendas }) => (
  <div className="vendedor-dashboard">
    <h3>Seu Desempenho de Vendas</h3>
    <div className="metrica">
      <span className="metrica-valor">R$ {totalVendas.toFixed(2)}</span>
      <span className="metrica-label">Total no Período</span>
    </div>
  </div>
);


function PDV() {
  const [produtos, setProdutos] = useState([]);
  const [carrinho, setCarrinho] = useState(JSON.parse(localStorage.getItem('pdvCarrinho')) || []);
  const [produtoSelecionadoParaModal, setProdutoSelecionadoParaModal] = useState(null);
  const { currentUser, userRole } = useAuth();
  const [totalVendasRecentes, setTotalVendasRecentes] = useState(0);

  // Função de busca com a NOVA LÓGICA DE DATAS
  const buscarVendasRecentes = useCallback(async () => {
    if (userRole === 'vendedor' && currentUser) {
      const hoje = new Date();

      // Data de início: Dia anterior, às 00:00:00
      const dataInicio = new Date(hoje);
      dataInicio.setDate(hoje.getDate() - 1);
      dataInicio.setHours(0, 0, 0, 0);

      // Data de fim: Dia seguinte, às 23:59:59
      const dataFim = new Date(hoje);
      dataFim.setDate(hoje.getDate() + 1);
      dataFim.setHours(23, 59, 59, 999);

      // A consulta agora usa o novo intervalo de datas
      const q = query(
        collection(db, "vendas"),
        where("vendedorId", "==", currentUser.uid),
        where("dataHora", ">=", dataInicio),
        where("dataHora", "<=", dataFim) // Condição de data final adicionada
      );

      try {
        const querySnapshot = await getDocs(q);
        let total = 0;
        querySnapshot.forEach((doc) => {
          total += doc.data().valorTotalVenda;
        });
        setTotalVendasRecentes(total);
      } catch (error) {
        console.error("Erro ao buscar métricas do vendedor:", error);
        // A mensagem de erro no console indicará a necessidade de um índice.
      }
    }
  }, [currentUser, userRole]);

  useEffect(() => {
    buscarVendasRecentes();
  }, [buscarVendasRecentes]);


  useEffect(() => {
    localStorage.setItem('pdvCarrinho', JSON.stringify(carrinho));
  }, [carrinho]);

  useEffect(() => {
    const buscarProdutos = async () => {
      try {
        const q = query(collection(db, "produtos"), where("ativoPDV", "==", true));
        const querySnapshot = await getDocs(q);
        const produtosDoFirestore = [];
        querySnapshot.forEach((doc) => {
          produtosDoFirestore.push({ id: doc.id, ...doc.data() });
        });
        setProdutos(produtosDoFirestore);
      } catch (error) {
        console.error("Erro ao buscar produtos:", error);
      }
    };
    buscarProdutos();
  }, []);

  const calcularTotal = () => {
    return carrinho.reduce((total, item) => total + item.precoTotalItem, 0);
  };

  const adicionarAoCarrinho = (itemNovo) => {
    setCarrinho(prevCarrinho => {
      const itemExistenteIndex = prevCarrinho.findIndex(item =>
        item.produtoId === itemNovo.produtoId &&
        item.tamanho === itemNovo.tamanho &&
        itemNovo.adicionaisSelecionados.every(ad1 => item.adicionaisSelecionados.some(ad2 => ad2.id === ad1.id)) &&
        item.adicionaisSelecionados.every(ad1 => itemNovo.adicionaisSelecionados.some(ad2 => ad2.id === ad1.id))
      );

      if (itemExistenteIndex > -1) {
        const updatedCarrinho = [...prevCarrinho];
        const itemAtual = updatedCarrinho[itemExistenteIndex];
        itemAtual.quantidade += itemNovo.quantidade;
        const precoUnitarioComAdicionais = itemAtual.precoUnitarioBase + itemAtual.adicionaisSelecionados.reduce((acc, adicional) => acc + adicional.preco, 0);
        itemAtual.precoTotalItem = precoUnitarioComAdicionais * itemAtual.quantidade;
        return updatedCarrinho;
      } else {
        return [...prevCarrinho, itemNovo];
      }
    });
    setProdutoSelecionadoParaModal(null);
  };

  const atualizarQuantidadeCarrinho = (produtoId, tamanho, adicionais, incremento) => {
    setCarrinho(prevCarrinho => {
      const updatedCarrinho = prevCarrinho.map(item => {
        if (
          item.produtoId === produtoId &&
          item.tamanho === tamanho &&
          item.adicionaisSelecionados.length === adicionais.length &&
          item.adicionaisSelecionados.every(ad1 => adicionais.some(ad2 => ad2.id === ad1.id))
        ) {
          const novaQuantidade = Math.max(0, item.quantidade + incremento);
          const precoUnitarioComAdicionais = item.precoUnitarioBase + item.adicionaisSelecionados.reduce((acc, adicional) => acc + adicional.preco, 0);
          return {
            ...item,
            quantidade: novaQuantidade,
            precoTotalItem: precoUnitarioComAdicionais * novaQuantidade
          };
        }
        return item;
      }).filter(item => item.quantidade > 0);
      return updatedCarrinho;
    });
  };

  const aumentarQuantidadeCarrinho = (produtoId, tamanho, adicionais) => {
    atualizarQuantidadeCarrinho(produtoId, tamanho, adicionais, 1);
  };

  const diminuirQuantidadeCarrinho = (produtoId, tamanho, adicionais) => {
    atualizarQuantidadeCarrinho(produtoId, tamanho, adicionais, -1);
  };

  const removerItemCarrinho = (produtoId, tamanho, adicionais) => {
    setCarrinho(prevCarrinho =>
      prevCarrinho.filter(item =>
        !(item.produtoId === produtoId &&
          item.tamanho === tamanho &&
          item.adicionaisSelecionados.length === adicionais.length &&
          item.adicionaisSelecionados.every(ad1 => adicionais.some(ad2 => ad2.id === ad1.id)))
      )
    );
  };

  const finalizarPedido = async () => {
    if (carrinho.length === 0) {
      alert('O carrinho está vazio!');
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      alert('Erro: Usuário não identificado. Por favor, faça login novamente.');
      return;
    }

    const venda = {
      dataHora: serverTimestamp(),
      itens: carrinho.map(item => ({
        produtoId: item.produtoId,
        nome: item.nome,
        tamanho: item.tamanho,
        precoUnitario: item.precoUnitarioBase,
        quantidade: item.quantidade,
        adicionais: item.adicionaisSelecionados,
        subtotal: item.precoTotalItem
      })),
      valorTotalVenda: calcularTotal(),
      vendedorId: user.uid,
      vendedorEmail: user.email,
    };

    try {
      await addDoc(collection(db, 'vendas'), venda);
      alert('Pedido finalizado com sucesso!');
      setCarrinho([]);
      localStorage.removeItem('pdvCarrinho');
      buscarVendasRecentes();
    } catch (e) {
      console.error("Erro ao adicionar documento: ", e);
      alert('Erro ao finalizar o pedido. Tente novamente.');
    }
  };

  return (
    <div className="pdv-app-container">
      <header className="pdv-header">
        <h1>MÁQUINA DE VENDAS</h1>
      </header>

      {userRole === 'vendedor' && <VendedorDashboard totalVendas={totalVendasRecentes} />}

      <main className="pdv-main-content">
        <section className="pdv-produtos-selector">
          <h2>SELECIONE O PRODUTO</h2>
          <div className="pdv-produtos-list-compact">
            {produtos.length === 0 ? (
              <p>Carregando produtos ou nenhum produto ativo para exibir...</p>
            ) : (
              produtos.map(produto => (
                <div
                  key={produto.id}
                  className="produto-list-item-compact"
                  onClick={() => setProdutoSelecionadoParaModal(produto)}
                >
                  {produto.nome}
                  <span className="produto-list-item-price">R$ {produto.opcoes[0]?.preco.toFixed(2)} (P)</span>
                  <span className="produto-list-item-price">R$ {produto.opcoes[1]?.preco.toFixed(2)} (G)</span>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="pdv-seu-pedido-compact">
          <h2>RESUMO DO PEDIDO</h2>
          <div className="pdv-tabela-wrapper">
            <table className="pdv-tabela-pedido">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Tam.</th>
                  <th>Quantidade</th>
                  <th>Preço</th>
                  <th>Excluir</th>
                </tr>
              </thead>
              <tbody>
                {carrinho.length === 0 ? (
                  <tr>
                    <td colSpan="5">Nenhum item.</td>
                  </tr>
                ) : (
                  carrinho.map((item, index) => (
                    <CarrinhoItem
                      key={`${item.produtoId}-${item.tamanho}-${JSON.stringify(item.adicionaisSelecionados)}`}
                      item={item}
                      onAumentarQuantidade={aumentarQuantidadeCarrinho}
                      onDiminuirQuantidade={diminuirQuantidadeCarrinho}
                      onRemoverItem={removerItemCarrinho}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
          <CarrinhoResumo total={calcularTotal()} onFinalizarPedido={finalizarPedido} />
        </section>
      </main>

      {produtoSelecionadoParaModal && (
        <ProdutoModal
          produto={produtoSelecionadoParaModal}
          onClose={() => setProdutoSelecionadoParaModal(null)}
          onAdicionar={adicionarAoCarrinho}
        />
      )}
    </div>
  );
}

export default PDV;
